import { ThemeProvider } from "@mui/material/styles";
import { jssPreset, StylesProvider } from "@mui/styles";
import * as Sentry from "@sentry/react";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserHistory } from "history";
import { create } from "jss";
import rtl from "jss-rtl";
import { enqueueSnackbar, SnackbarProvider } from "notistack";
import type React from "react";
import { useTranslation } from "react-i18next";
import { Router } from "react-router-dom";
import GlobalStyles from "src/components/GlobalStyles";
import GoogleAnalytics from "src/components/GoogleAnalytics";
import ScrollReset from "src/components/ScrollReset";
import { AuthProvider } from "src/contexts/JWTAuthContext";
import useSettings from "src/hooks/useSettings";
import routes from "src/routes";
import { ErrorBoundary } from "./components/Error/ErrorBoundary";
// import { DomainProvider } from "./domains";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/fr";
import updateLocale from "dayjs/plugin/updateLocale";
import { RouterWithAuth } from "src/components/RouterWithAuth";
import { TranslationProvider } from "src/design-system/core/TranslationContext";
import { getLocale } from "src/design-system/i18n";
import { createTheme } from "src/theme";
import { InitSentry } from "./components/Error/SentryUtils";
import { UserContextSentry } from "./components/Error/UserContextSentry";
import { useDocumentDirection } from "./hooks/useDocumentDirection";

const jss = create({ plugins: [...jssPreset().plugins, rtl()] });
const history = createBrowserHistory();
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
    queryCache: new QueryCache({
        onError: (_error, query) => {
            const variant = query?.meta?.errorVariant ?? "error";
            if (query.meta?.errorMessage) {
                enqueueSnackbar(query.meta.errorMessage as string, {
                    variant: variant as "error" | "default" | "success" | "warning" | "info",
                });
            }
        },
    }),
});

const locale = getLocale();
dayjs.locale(locale);
dayjs.extend(updateLocale);
dayjs.updateLocale("en", {
    // Sunday = 0, Monday = 1.
    weekStart: 1,
});

InitSentry();

const App: React.FC = () => {
    const { settings } = useSettings();
    useDocumentDirection();
    const { t } = useTranslation();

    const theme = createTheme({
        direction: settings.direction,
        responsiveFontSizes: settings.responsiveFontSizes,
        theme: settings.theme,
    });
    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
                <StylesProvider jss={jss}>
                    <TranslationProvider
                        translationFn={t}
                        globals={{
                            common: "common:",
                            raw: undefined,
                        }}
                    >
                        <SnackbarProvider dense maxSnack={3} autoHideDuration={8000}>
                            <Router history={history}>
                                <QueryClientProvider client={queryClient}>
                                    {["release", "dev", "development"].includes(
                                        import.meta.env.VITE_FLYNT_ENV
                                    ) && <ReactQueryDevtools initialIsOpen={false} />}
                                    <AuthProvider>
                                        <GlobalStyles />
                                        <ScrollReset />
                                        <GoogleAnalytics />
                                        <UserContextSentry />
                                        <ErrorBoundary>
                                            <RouterWithAuth routes={routes} />
                                        </ErrorBoundary>
                                    </AuthProvider>
                                </QueryClientProvider>
                            </Router>
                        </SnackbarProvider>
                    </TranslationProvider>
                </StylesProvider>
            </LocalizationProvider>
        </ThemeProvider>
    );
};

export default Sentry.withProfiler(App);
